import { useTranslation } from 'react-i18next';
import { AccountReliabilityBadge } from '@/components/admin/AccountReliabilityBadge';
import { cn } from '@/lib/utils';
import { resolveUserReliabilityListDisplay } from '@/lib/campaigns/resolveUserReliabilityListDisplay';
import type { UserReliabilityAccount } from '@/lib/campaigns/resolveUserReliabilityListDisplay';

type UserReliabilityAccountsProps = {
  accounts?: UserReliabilityAccount[] | null;
};

function platformLabel(
  t: (key: string) => string,
  platform: string | null | undefined,
): string {
  if (platform === 'instagram' || platform === 'tiktok' || platform === 'youtube') {
    return t(`admin.users.reliability.platforms.${platform}`);
  }
  return platform || '';
}

export function UserReliabilitySummary({
  accounts,
}: UserReliabilityAccountsProps) {
  const display = resolveUserReliabilityListDisplay(accounts);

  if (display.kind === 'blank') return null;

  if (display.kind === 'warning') {
    const worst = (accounts || [])
      .filter((account) => account?.status === 'low')
      .reduce<UserReliabilityAccount | null>((current, account) => {
        if (current == null) return account;
        const currentScore = typeof current.score === 'number' ? current.score : Infinity;
        const nextScore = typeof account?.score === 'number' ? account.score : Infinity;
        return nextScore < currentScore ? account : current;
      }, null);

    return <AccountReliabilityBadge reliability={worst} />;
  }

  return (
    <div
      data-testid="user-reliability-scores"
      className="flex flex-wrap gap-1"
    >
      {display.parts.map((part) => (
        <span
          key={`${part.platformLabel}-${part.score}`}
          className="inline-flex items-center rounded-md border border-border bg-muted/80 px-1.5 py-0.5 text-xs font-medium"
        >
          <span className="mr-1 text-muted-foreground">{part.platformLabel}</span>
          <span className="tabular-nums font-semibold text-foreground">{part.score}</span>
        </span>
      ))}
    </div>
  );
}

export function UserReliabilityAccounts({
  accounts,
}: UserReliabilityAccountsProps) {
  const { t } = useTranslation();
  const rows = accounts || [];
  if (rows.length === 0) return null;

  const display = resolveUserReliabilityListDisplay(rows);
  const warn = display.kind === 'warning';

  return (
    <div className="space-y-2" data-testid="user-reliability-accounts">
      <p className="text-sm font-medium">{t('admin.users.reliability.title')}</p>
      <div
        className={cn(
          'space-y-2 rounded-lg border p-3',
          warn ? 'border-amber-500/50 bg-amber-500/10' : 'border-border',
        )}
      >
        {rows.map((account, index) => {
          const handle = account?.username ? `@${account.username}` : '';
          const label = [platformLabel(t, account?.platform), handle]
            .filter(Boolean)
            .join(' ');
          return (
            <div
              key={`${account?.platform || 'unknown'}-${account?.username || index}`}
              className="flex items-center justify-between gap-3"
            >
              <span className="text-sm font-medium truncate">{label || '—'}</span>
              <AccountReliabilityBadge reliability={account} variant="hero" />
            </div>
          );
        })}
      </div>
    </div>
  );
}
